import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, DollarSign, Percent, Link2, FileText } from "lucide-react";

interface Deal {
  id: string;
  deal_title: string;
  payment_amount: number | null;
  payments_remaining: number | null;
  balloon_amount: number | null;
  balloon_due_date: string | null;
  estimated_property_value: number | null;
  property_address: string | null;
  current_balance: number | null;
}

interface InvestorPricingCalculatorProps {
  onGenerateReview?: (data: any) => void;
}

export function InvestorPricingCalculator({ onGenerateReview }: InvestorPricingCalculatorProps) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  
  const [yield_, setYield] = useState([12.5]); // Default 12.5%
  const [itv, setItv] = useState([70]); // Default 70%
  const [autoAdjust, setAutoAdjust] = useState(true); // Auto-adjust linked value
  
  // Local input states for typing
  const [investorPriceInput, setInvestorPriceInput] = useState("");
  const [itvInput, setItvInput] = useState("");
  
  // Contract details
  const [currentBalance, setCurrentBalance] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(1000);
  const [remainingMonths, setRemainingMonths] = useState(120);
  const [balloonPayment, setBalloonPayment] = useState(0);
  const [propertyValue, setPropertyValue] = useState(150000);

  // Fetch deals on mount
  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('id, deal_title, payment_amount, payments_remaining, balloon_amount, balloon_due_date, estimated_property_value, property_address, current_balance')
      .order('deal_title');
    
    if (error) {
      console.error('Error fetching deals:', error);
      return;
    }
    
    setDeals(data || []);
  };

  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    const deal = deals.find(d => d.id === dealId);
    
    if (deal) {
      setCurrentBalance(deal.current_balance || 0);
      setMonthlyPayment(deal.payment_amount || 0);
      setRemainingMonths(deal.payments_remaining || 0);
      setBalloonPayment(deal.balloon_amount || 0);
      setPropertyValue(deal.estimated_property_value || 0);
    }
  };

  // Calculate investor pay price based on yield
  const calculateInvestorPrice = (yieldRate: number) => {
    const monthlyRate = yieldRate / 100 / 12;
    
    // Present value of monthly payments
    let pvPayments = 0;
    if (monthlyRate > 0) {
      pvPayments = monthlyPayment * ((1 - Math.pow(1 + monthlyRate, -remainingMonths)) / monthlyRate);
    } else {
      pvPayments = monthlyPayment * remainingMonths;
    }
    
    // Present value of balloon payment
    const pvBalloon = balloonPayment / Math.pow(1 + monthlyRate, remainingMonths);
    
    return pvPayments + pvBalloon;
  };

  // Calculate maximum price based on ITV
  const calculateMaxPrice = (itvRatio: number) => {
    return propertyValue * (itvRatio / 100);
  };

  const investorPrice = calculateInvestorPrice(yield_[0]);
  const maxItvPrice = calculateMaxPrice(itv[0]);
  const actualItv = propertyValue > 0 ? (investorPrice / propertyValue) * 100 : 0;
  
  // Check if deal works (investor price doesn't exceed ITV limit)
  const dealWorks = investorPrice <= maxItvPrice;

  const handleGenerateReview = () => {
    const selectedDeal = deals.find(d => d.id === selectedDealId);
    if (!selectedDeal || !onGenerateReview) return;
    
    onGenerateReview({
      dealTitle: selectedDeal.deal_title || "Investment Opportunity",
      propertyAddress: selectedDeal.property_address || "Property Address Not Available",
      currentBalance: currentBalance,
      monthlyPayment: monthlyPayment,
      paymentsRemaining: remainingMonths,
      balloonAmount: balloonPayment,
      balloonDueDate: selectedDeal.balloon_due_date || "TBD",
      propertyValue: propertyValue,
      investorPrice: investorPrice,
      targetYield: yield_[0],
      itv: actualItv,
    });
  };

  // Handle yield change and auto-adjust ITV
  const handleYieldChange = (value: number[]) => {
    setYield(value);
    if (autoAdjust && propertyValue > 0) {
      const newInvestorPrice = calculateInvestorPrice(value[0]);
      const newItv = (newInvestorPrice / propertyValue) * 100;
      setItv([Math.min(100, Math.max(20, newItv))]);
    }
  };

  // Handle direct investor price input
  const handleInvestorPriceInput = (value: string) => {
    setInvestorPriceInput(value);
  };

  const applyInvestorPriceChange = () => {
    const price = Number(investorPriceInput);
    if (!price || price <= 0 || propertyValue <= 0) {
      setInvestorPriceInput("");
      return;
    }
    
    // Binary search to find yield that produces target price - expanded range
    let low = 0.5, high = 30; // Allow yields from 0.5% to 30%
    let bestYield = yield_[0];
    let bestDiff = Infinity;
    
    for (let i = 0; i < 50; i++) { // More iterations for better accuracy
      const mid = (low + high) / 2;
      const calculatedPrice = calculateInvestorPrice(mid);
      const diff = Math.abs(calculatedPrice - price);
      
      if (diff < bestDiff) {
        bestDiff = diff;
        bestYield = mid;
      }
      
      if (calculatedPrice > price) {
        low = mid;
      } else {
        high = mid;
      }
      
      // Break if we're close enough
      if (diff < 1) break;
    }
    
    setYield([Math.min(30, Math.max(0.5, bestYield))]);
    
    if (autoAdjust) {
      const newItv = (price / propertyValue) * 100;
      setItv([Math.min(100, Math.max(20, newItv))]);
    }
    
    setInvestorPriceInput("");
  };

  // Handle ITV change and auto-adjust yield
  const handleItvChange = (value: number[]) => {
    setItv(value);
    if (autoAdjust && propertyValue > 0) {
      const targetPrice = calculateMaxPrice(value[0]);
      // Binary search to find yield that produces target price - expanded range
      let low = 0.5, high = 30;
      let bestYield = yield_[0];
      let bestDiff = Infinity;
      
      for (let i = 0; i < 50; i++) {
        const mid = (low + high) / 2;
        const price = calculateInvestorPrice(mid);
        const diff = Math.abs(price - targetPrice);
        
        if (diff < bestDiff) {
          bestDiff = diff;
          bestYield = mid;
        }
        
        if (price > targetPrice) {
          low = mid;
        } else {
          high = mid;
        }
        
        if (diff < 1) break;
      }
      
      setYield([Math.min(30, Math.max(0.5, bestYield))]);
    }
  };

  // Handle direct ITV percentage input
  const handleItvInputChange = (value: string) => {
    setItvInput(value);
  };

  const applyItvChange = () => {
    const itvPercent = Number(itvInput);
    if (!itvPercent || itvPercent < 20 || itvPercent > 100) {
      setItvInput("");
      return;
    }
    
    const clampedItv = Math.min(100, Math.max(20, itvPercent));
    setItv([clampedItv]);
    
    if (autoAdjust && propertyValue > 0) {
      const targetPrice = calculateMaxPrice(clampedItv);
      let low = 0.5, high = 30;
      let bestYield = yield_[0];
      let bestDiff = Infinity;
      
      for (let i = 0; i < 50; i++) {
        const mid = (low + high) / 2;
        const price = calculateInvestorPrice(mid);
        const diff = Math.abs(price - targetPrice);
        
        if (diff < bestDiff) {
          bestDiff = diff;
          bestYield = mid;
        }
        
        if (price > targetPrice) {
          low = mid;
        } else {
          high = mid;
        }
        
        if (diff < 1) break;
      }
      
      setYield([Math.min(30, Math.max(0.5, bestYield))]);
    }
    
    setItvInput("");
  };

  return (
    <div className="space-y-6">
      {/* Deal Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Auto-Populate from Deal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Select Deal</Label>
            <Select value={selectedDealId} onValueChange={handleDealSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a deal to auto-fill..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle>Contract Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="space-y-2">
            <Label>Current Balance</Label>
            <Input
              type="number"
              value={currentBalance}
              onChange={(e) => setCurrentBalance(Number(e.target.value))}
              placeholder="Current balance"
              className="font-semibold"
            />
          </div>
          <div className="space-y-2">
            <Label>Monthly Payment</Label>
            <Input
              type="number"
              value={monthlyPayment}
              onChange={(e) => setMonthlyPayment(Number(e.target.value))}
              placeholder="Monthly payment"
            />
          </div>
          <div className="space-y-2">
            <Label>Remaining Months</Label>
            <Input
              type="number"
              value={remainingMonths}
              onChange={(e) => setRemainingMonths(Number(e.target.value))}
              placeholder="Months"
            />
          </div>
          <div className="space-y-2">
            <Label>Balloon Payment</Label>
            <Input
              type="number"
              value={balloonPayment}
              onChange={(e) => setBalloonPayment(Number(e.target.value))}
              placeholder="Balloon amount"
            />
          </div>
          <div className="space-y-2">
            <Label>Property Value</Label>
            <Input
              type="number"
              value={propertyValue}
              onChange={(e) => setPropertyValue(Number(e.target.value))}
              placeholder="Property value"
            />
          </div>
        </CardContent>
      </Card>

      {/* Auto-Adjust Toggle */}
      <Card className="border-2 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-purple-600" />
              <Label htmlFor="auto-adjust" className="font-semibold">Link Yield & ITV</Label>
            </div>
            <Switch
              id="auto-adjust"
              checked={autoAdjust}
              onCheckedChange={setAutoAdjust}
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {autoAdjust 
              ? "Adjusting one slider automatically updates the other" 
              : "Sliders move independently"}
          </p>
        </CardContent>
      </Card>

      {/* Interactive Sliders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Yield Column */}
        <Card className="border-2 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" />
              Investor Yield
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="text-lg">Target Yield</Label>
                <span className="text-3xl font-bold text-primary">{yield_[0].toFixed(1)}%</span>
              </div>
              <Slider
                value={yield_}
                onValueChange={handleYieldChange}
                min={0.5}
                max={30}
                step={0.1}
                className="py-4"
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>0.5%</span>
                <span>30%</span>
              </div>
            </div>

            <div className="pt-4 border-t space-y-3">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Investor Pay Price:</Label>
                <Input
                  type="number"
                  value={investorPriceInput || Math.round(investorPrice)}
                  onChange={(e) => handleInvestorPriceInput(e.target.value)}
                  onBlur={applyInvestorPriceChange}
                  onKeyDown={(e) => e.key === 'Enter' && applyInvestorPriceChange()}
                  placeholder={`$${Math.round(investorPrice).toLocaleString()}`}
                  className="text-xl font-bold"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Actual ITV:</span>
                <span className="text-lg font-semibold">
                  {actualItv.toFixed(1)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ITV Column */}
        <Card className="border-2 border-secondary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Investment to Value
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-lg">Maximum ITV (%)</Label>
                <Input
                  type="number"
                  value={itvInput || itv[0]}
                  onChange={(e) => handleItvInputChange(e.target.value)}
                  onBlur={applyItvChange}
                  onKeyDown={(e) => e.key === 'Enter' && applyItvChange()}
                  placeholder={`${itv[0]}%`}
                  className="text-xl font-bold"
                  min={20}
                  max={100}
                />
              </div>
              <Slider
                value={itv}
                onValueChange={handleItvChange}
                min={20}
                max={100}
                step={1}
                className="py-4"
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>20%</span>
                <span>100%</span>
              </div>
            </div>

            {onGenerateReview && selectedDealId && (
              <div className="pt-4 border-t">
                <Button onClick={handleGenerateReview} className="w-full flex items-center gap-2" size="lg">
                  <FileText className="h-4 w-4" />
                  Generate Investor Review
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
